/**
 * The links on one node: the rows for the links it already has, and the form
 * that adds another.
 *
 * A link points at an address, at another step of the map, or at both, and one
 * of the two it must keep - a row left with neither would be a button that goes
 * nowhere. The address itself is written in the host's own field when the host
 * offers one (`env.LinkField`, the builder's link picker) and in a plain text
 * box when it does not (the CRM), with the same rule deciding what may be
 * stored either way.
 *
 * Lifted out of NodeInspector.tsx unchanged: that file had grown past the size
 * where anybody could find anything in it, and the links are the part of a node
 * that stands on its own. The step picker is the widgets' own pick-one list
 * (`../combo/`), never a native select.
 */
import { useId, useState, type ReactElement } from "react";
import { Combo, type PickChoice } from "../combo/index.js";
import { IconPlus, IconTrash, IconX } from "./icons.js";
import { AddText, CommitText, Field, IconButton, Section, nodeOptions, type EditorEnv, type Patch } from "./inspectorFields.js";
import {
  FLOW_LIMITS,
  isSafeHref,
  makeFlowId,
  readText,
  updateNode,
  writeText,
  type FlowLink,
  type FlowNode,
} from "./model.js";

function withoutHref(link: FlowLink): FlowLink {
  const out: FlowLink = { id: link.id, label: link.label };
  if (link.nodeId) out.nodeId = link.nodeId;
  return out;
}

/** The step picker's choices: "none" first, then every other node. */
function nodeChoices(none: string, others: ReadonlyArray<{ id: string; title: string }>): PickChoice[] {
  return [{ value: "", label: none }, ...others.map((o) => ({ value: o.id, label: o.title }))];
}

function withoutNode(link: FlowLink): FlowLink {
  const out: FlowLink = { id: link.id, label: link.label };
  if (link.href) out.href = link.href;
  return out;
}

type NodeOption = { id: string; title: string };

/** One stored link. A link must keep an address or a node, so clearing the last one is refused. */
function LinkRow({
  env,
  link,
  others,
  onChange,
  onRemove,
}: {
  env: EditorEnv;
  link: FlowLink;
  others: NodeOption[];
  onChange: (fn: (link: FlowLink) => FlowLink) => void;
  onRemove: () => void;
}): ReactElement {
  const { labels, locale, textLocale, textDefaultLocale, LinkField } = env;
  const [invalid, setInvalid] = useState(false);
  const errorId = useId();
  // One rule for the address, wherever it came from: typed into the plain box
  // or chosen in a host's picker. A link has to keep an address or a node, so
  // emptying the last one it has is refused rather than silently accepted.
  const commitHref = (value: string): void => {
    const href = value.trim();
    if (!href ? !link.nodeId : !isSafeHref(href)) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    onChange((x) => (href ? { ...x, href } : withoutHref(x)));
  };
  return (
    <li className="wa-fm__card">
      <CommitText
        value={readText(link.label, locale)}
        ariaLabel={labels.linkLabel}
        placeholder={labels.linkLabel}
        maxLength={FLOW_LIMITS.label}
        onCommit={(v) => onChange((x) => ({ ...x, label: writeText(x.label, v.trim(), textLocale, textDefaultLocale) }))}
      />
      {LinkField ? (
        <LinkField value={link.href ?? ""} placeholder={labels.linkHref} onChange={commitHref} />
      ) : (
        <CommitText
          value={link.href ?? ""}
          ariaLabel={labels.linkHref}
          placeholder={labels.linkHref}
          maxLength={FLOW_LIMITS.href}
          invalid={invalid}
          describedBy={invalid ? errorId : undefined}
          onDraft={() => setInvalid(false)}
          onCommit={commitHref}
        />
      )}
      {invalid ? (
        <p id={errorId} className="wa-fm__error" role="alert">
          {labels.linkInvalid}
        </p>
      ) : null}
      <div className="wa-fm__row">
        <Combo
          className="wa-fm__pickw"
          buttonClassName="wa-fm__pick"
          fitPanel
          label={labels.linkNode}
          value={link.nodeId ?? ""}
          choices={nodeChoices(labels.noNode, others)}
          searchLabel={labels.pickSearch}
          emptyText={labels.pickNoMatch}
          onChange={(id) => {
            if (!id && !link.href) return;
            onChange((x) => (id ? { ...x, nodeId: id } : withoutNode(x)));
          }}
        />
        <IconButton label={labels.remove} danger onClick={onRemove}>
          <IconTrash size={14} />
        </IconButton>
      </div>
    </li>
  );
}

/** The "add link" form: a label, an address and/or a node of the map. */
function NewLink({ env, others, onAdd }: { env: EditorEnv; others: NodeOption[]; onAdd: (link: Omit<FlowLink, "id">) => void }): ReactElement {
  const { labels, textLocale, textDefaultLocale, LinkField } = env;
  const [label, setLabel] = useState("");
  const [href, setHref] = useState("");
  const [nodeId, setNodeId] = useState("");
  const [touched, setTouched] = useState(false);
  const errorId = useId();
  const h = href.trim();
  const hrefOk = !h || isSafeHref(h);
  const ready = h ? hrefOk : nodeId !== "";
  const showError = touched && !hrefOk;
  const add = (): void => {
    if (!ready) {
      setTouched(true);
      return;
    }
    const link: Omit<FlowLink, "id"> = { label: writeText(undefined, label.trim(), textLocale) };
    if (h) link.href = h;
    if (nodeId) link.nodeId = nodeId;
    onAdd(link);
    setLabel("");
    setHref("");
    setNodeId("");
    setTouched(false);
  };
  return (
    <div className="wa-fm__card">
      <input
        type="text"
        className="wa-fm__input"
        value={label}
        placeholder={labels.linkLabel}
        aria-label={labels.linkLabel}
        maxLength={FLOW_LIMITS.label}
        onChange={(e) => setLabel(e.target.value)}
      />
      {LinkField ? (
        <LinkField
          value={href}
          placeholder={labels.linkHref}
          onChange={(v) => {
            setHref(v);
            setTouched(false);
          }}
        />
      ) : (
        <input
          type="text"
          inputMode="url"
          className="wa-fm__input"
          value={href}
          placeholder={labels.linkHref}
          aria-label={labels.linkHref}
          aria-invalid={showError ? true : undefined}
          aria-describedby={showError ? errorId : undefined}
          maxLength={FLOW_LIMITS.href}
          onChange={(e) => {
            setHref(e.target.value);
            setTouched(false);
          }}
          onBlur={() => setTouched(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
        />
      )}
      {showError ? (
        <p id={errorId} className="wa-fm__error" role="alert">
          {labels.linkInvalid}
        </p>
      ) : null}
      <div className="wa-fm__row">
        <Combo
          className="wa-fm__pickw"
          buttonClassName="wa-fm__pick"
          fitPanel
          label={labels.linkNode}
          value={nodeId}
          choices={nodeChoices(labels.noNode, others)}
          searchLabel={labels.pickSearch}
          emptyText={labels.pickNoMatch}
          onChange={setNodeId}
        />
        <button type="button" className="wa-fm__btn wa-fm__btn--sm" disabled={!ready} onClick={add}>
          <IconPlus size={14} />
          {labels.addLink}
        </button>
      </div>
    </div>
  );
}

export function LinksEditor({ env, node, patch }: { env: EditorEnv; node: FlowNode; patch: Patch }): ReactElement {
  const links = node.links ?? [];
  const others = nodeOptions(env.doc, env.locale, env.labels, node.id);
  const setLinks = (fn: (list: FlowLink[]) => FlowLink[]): void =>
    patch((n) => {
      const next = fn(n.links ?? []);
      return { links: next.length ? next : null };
    });
  return (
    <Section title={env.labels.links}>
      {links.length ? (
        <ul className="wa-fm__rows">
          {links.map((l) => (
            <LinkRow
              key={l.id}
              env={env}
              link={l}
              others={others}
              onChange={(fn) => setLinks((list) => list.map((x) => (x.id === l.id ? fn(x) : x)))}
              onRemove={() => setLinks((list) => list.filter((x) => x.id !== l.id))}
            />
          ))}
        </ul>
      ) : null}
      {links.length < FLOW_LIMITS.linksPerNode ? (
        <NewLink env={env} others={others} onAdd={(link) => setLinks((list) => [...list, { id: makeFlowId("l", new Set(list.map((x) => x.id)), true), ...link }])} />
      ) : null}
    </Section>
  );
}

